import { Rectangle, Sprite, Texture } from "pixi.js"
import { shadeTint } from "./building_shade"

/** The shade's line on tall art is softened over this many art px each way. */
export const CLIMB_EDGE = 2

/** Where a climb shade's bands begin, in rows of art `height` px whose shade line is `line` rows down. */
export const climbBandTops = (line: number, height: number): number[] =>
    [line - CLIMB_EDGE, line + CLIMB_EDGE].map(top => Math.max(0, Math.min(height - 1, Math.round(top))))

/**
 * The buildings' shade on tall art (a tree), as high up it as the shade climbs (sprite_shade.ts
 * shadeClimb): the art below that line drawn over itself again, tinted as the shade layer darkens the
 * ground -- in two bands a few px apart for a soft edge: the upper at half the shade, the lower at all
 * of it (opaque, each replacing the art it covers, so they never compound). The bands are sprites of their own beside the art's, never its children: the art's sprites come
 * from a pool and are handed on to other tiles, and a child would go with them. So they follow the art's
 * place, sway, fall and fade a frame at a time, and hide once the art is no longer the same art.
 */
export class ClimbShade {
    private readonly source: Texture
    private readonly bands: { sprite: Sprite; texture: Texture; top: number }[]

    /** `line`: the art's row (from its texture's top) where the shade ends; undefined when its art can't be cut. */
    static create(art: Sprite, line: number, owned: () => boolean): ClimbShade | undefined {
        const texture = art.texture
        if (!texture?.valid || texture.trim || texture.rotate) return undefined
        return new ClimbShade(art, line, owned)
    }

    private constructor(readonly art: Sprite, readonly line: number, private readonly owned: () => boolean) {
        this.source = art.texture
        this.bands = climbBandTops(line, this.source.frame.height).map(top => {
            const texture = this.cut(top)
            return { sprite: ClimbShade.band(texture), texture, top }
        })
    }

    /** The art from row `top` down. */
    private cut(top: number): Texture {
        const { frame, baseTexture } = this.source
        return new Texture(baseTexture, new Rectangle(frame.x, frame.y + top, frame.width, frame.height - top))
    }

    private static band(texture: Texture): Sprite {
        const sprite = new Sprite(texture)
        sprite.visible = false
        return sprite
    }

    /** The bands as they stand: a map's surface hands them on with its art (MapRenderingManager.ownedArt). */
    displayObjects(): Sprite[] {
        return this.bands.map(band => band.sprite).filter(sprite => !sprite.destroyed)
    }

    /** Shown at `strength` (0: not at all, 1: the shade's full darkening), tinted `color`, where the art is now. */
    sync(strength: number, color: number): void {
        const art = this.art
        const live = strength > 0 && !art.destroyed && !!art.parent && art.texture === this.source && this.owned()
        const n = this.bands.length
        this.bands.forEach((band, i) => {
            // A container destroyed with its children took the band along: every later frame
            // would throw adding it back, so lay a new one.
            if (band.sprite.destroyed) {
                if (!live) return
                if (!band.texture.valid) band.texture = this.cut(band.top)
                band.sprite = ClimbShade.band(band.texture)
            }
            const { sprite, top } = band
            sprite.visible = live && art.visible && art.renderable
            if (!sprite.visible) return
            if (sprite.parent !== art.parent) art.parent.addChild(sprite)
            const z = art.zIndex + 0.01
            // A zIndex write re-sorts the whole surface: only on a change.
            if (sprite.zIndex !== z) sprite.zIndex = z
            const height = this.source.frame.height
            sprite.anchor.set(art.anchor.x, (art.anchor.y * height - top) / (height - top))
            sprite.position.copyFrom(art.position)
            sprite.scale.copyFrom(art.scale)
            sprite.skew.copyFrom(art.skew)
            sprite.pivot.copyFrom(art.pivot)
            sprite.rotation = art.rotation
            sprite.alpha = art.alpha
            const tint = shadeTint(0xffffff, Math.min(1, strength) * (i + 1) / n, color)
            if (sprite.tint !== tint) sprite.tint = tint
        })
    }

    /** Whether this is the shade of `art` as it is now, ending at `line`. */
    isFor(art: Sprite, line: number): boolean {
        return this.art === art && this.line === line && art.texture === this.source
    }

    destroy(): void {
        for (const { sprite, texture } of this.bands) {
            if (!sprite.destroyed) {
                sprite.parent?.removeChild(sprite)
                sprite.destroy()
            }
            texture.destroy(false)
        }
    }
}
